import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TypeSection,
  DurationSection,
  IntensitySection,
  MoodSection,
  NoteSection,
  ShareSection,
} from "@/components/activity/FormSections";
import { useAddActivityForm } from "@/hooks/useAddActivityForm";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { fadeUp } from "@/lib/motion";

export default function AddActivity() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t } = useLanguage();
  const form = useAddActivityForm();

  return (
    <div className="space-y-4">
      <motion.header {...fadeUp(0)} className="pt-2 flex items-center gap-3">
        <button
          data-testid="add-activity-back-button"
          onClick={() => navigate(-1)}
          aria-label={t("addActivity.backAria")}
          className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("addActivity.title")}
          </h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {t("addActivity.subtitle")}
          </p>
        </div>
      </motion.header>

      <TypeSection value={form.type} onChange={form.setType} delay={0.05} />

      <DurationSection
        duration={form.duration}
        customMode={form.customMode}
        customValue={form.customValue}
        onPreset={(d) => {
          form.setCustomMode(false);
          form.setDuration(d);
        }}
        onCustomMode={() => form.setCustomMode(true)}
        onCustomValue={form.setCustomValue}
        delay={0.1}
      />

      <IntensitySection value={form.intensity} onChange={form.setIntensity} delay={0.15} />

      <MoodSection
        title={t("addActivity.moodBeforeSection")}
        testPrefix="add-activity-mood-before"
        value={form.moodBefore}
        onChange={form.setMoodBefore}
        delay={0.2}
      />

      <MoodSection
        title={t("addActivity.moodAfterSection")}
        testPrefix="add-activity-mood-after"
        value={form.moodAfter}
        onChange={form.setMoodAfter}
        delay={0.25}
      />

      <NoteSection value={form.note} onChange={form.setNote} delay={0.3} />

      <ShareSection
        value={form.share}
        onChange={form.setShare}
        isPrivate={profile.privacy === "private"}
        delay={0.35}
      />

      <motion.div {...fadeUp(0.4)} className="sticky bottom-24 pt-1">
        <Button
          data-testid="add-activity-save-button"
          onClick={form.save}
          disabled={form.saving}
          className="w-full py-6 rounded-xl bg-cyan-300 text-slate-950 text-base font-semibold hover:bg-cyan-200 active:scale-[0.98] shadow-[0_0_0_1px_rgba(34,211,238,0.25),0_12px_40px_rgba(34,211,238,0.2)] disabled:opacity-60"
        >
          {form.saving ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            t("addActivity.save")
          )}
        </Button>
      </motion.div>
    </div>
  );
}
